// Shared mapping from expo-local-authentication's error vocabulary into the
// Vida biometric reason codes. Kept in one place so an expo rename (e.g. a
// case flip like the historical 'user_cancel' churn) is a single edit, and
// enrollment vs. sign-in stay in lockstep. Callers narrow via
// `Extract<BiometricPromptErrorReason, ...>` when they only surface a subset.

export type BiometricPromptErrorReason =
  | 'biometric_cancelled'
  | 'biometric_fallback_chosen'
  | 'biometric_not_enrolled'
  | 'biometric_lockout'
  | 'biometric_mismatch'
  | 'biometric_unavailable'
  | 'biometric_failed';

export const mapExpoAuthError = (
  error: string | undefined
): BiometricPromptErrorReason => {
  switch (error) {
    case 'user_cancel':
    case 'app_cancel':
    case 'system_cancel':
    // `timeout` is the user walking away from the prompt, not a hardware
    // failure — grouping it here keeps `biometric_failed` reserved for
    // genuinely unexplained device problems.
    case 'timeout':
      return 'biometric_cancelled';
    case 'user_fallback':
      // iOS-only: the user explicitly tapped "Use Passcode". That's an
      // opt-in to the device-credential fallback, distinct from cancelling.
      return 'biometric_fallback_chosen';
    case 'not_enrolled':
    case 'passcode_not_set':
      return 'biometric_not_enrolled';
    case 'lockout':
      return 'biometric_lockout';
    case 'authentication_failed':
      // A real biometric was presented but didn't match. Distinct from
      // `biometric_failed` so ME-1745 attribution can separate "user's finger
      // didn't match, just retry" from "hardware / device is broken".
      return 'biometric_mismatch';
    case 'not_available':
      // Biometric hardware is unreachable (module offline, disabled by MDM).
      // Distinct from `biometric_failed` for the same reason as above.
      return 'biometric_unavailable';
    // Residual bucket: `no_space`, `unable_to_process`, `invalid_context`,
    // `unknown`, and anything expo adds in the future. Rare enough to lump
    // together; deliberately kept as the fallthrough so a new expo code
    // doesn't silently vanish.
    default:
      return 'biometric_failed';
  }
};
